import { send } from './client';
import type { AlertType, Severity } from './common';
import { query, type Cursor, type CursorPage } from './paging';

/**
 * Where an alert is in its life: needs action (open, nobody took it), handling (open, someone took it), resolved
 * (the condition cleared by itself, or a person closed it).
 */
export const ALERT_STATES = ['needs_action', 'handling', 'resolved'] as const;
export type AlertState = (typeof ALERT_STATES)[number];
export const isAlertState = (v: string | null): v is AlertState => (ALERT_STATES as readonly (string | null)[]).includes(v);
export const SEVERITIES: readonly Severity[] = ['critical', 'warning', 'info'];
/** Every type, in the order the Jenis filter lists them (readers, cylinders and delivery notes, then the system's). */
export const ALERT_TYPES: readonly AlertType[] = [
  'GATEWAY_OFFLINE',
  'READER_ERROR',
  'CLOCK_UNSYNCED',
  'CLONE_SUSPECT',
  'TAG_REWRITTEN',
  'SEEN_WITHOUT_SHIPMENT',
  'SHIPMENT_MISSING',
  'SHIPMENT_EXTRA_TAG',
  'SHIPMENT_OFF_NETWORK',
  'TEST_DUE',
  'TAG_SUSPECTED_DEAD',
  'STOCK_GAP',
  'ORDER_SLA_BREACHED',
  'ORDER_OVERCHARGED',
  'ORDER_CONFIRMATION_LOCKED',
  'EXCHANGE_FOREIGN_EMPTY',
  'EXCHANGE_MISMATCH',
  'HOUSEHOLD_HOARDING',
  'LOST_CYLINDER_SEEN',
  'HANDOVER_NOT_LEFT_HUB',
  'COURIER_MISMATCH_PATTERN',
  'EMPTY_NOT_RETURNED',
  'ORDER_CONFIRMED_WITHOUT_CODE',
  'DELIVERY_FAR_FROM_HOME',
  'COURIER_MULTI_DEVICE',
  'RETEST_OVERDUE_AT_HOUSEHOLD',
  'SEQ_GAP_UNRECOVERABLE',
  'BAD_PAYLOAD',
];
/** Raised by the system itself, never at a place: only a national account sees them. */
export const SYSTEM_ALERT_TYPES: readonly AlertType[] = ['SEQ_GAP_UNRECOVERABLE', 'BAD_PAYLOAD'];

export interface AlertPerson { id: number; name: string }

/**
 * One alert as GET /alerts lists it. `title` and `body` are rendered by the server when read, from the CURRENT names
 * of the objects it is about (a renamed or moved reader reads correctly); the linked objects come with it.
 */
export interface Alert {
  id: number;
  type: AlertType;
  severity: Severity;
  state: AlertState;
  title: string;
  body: string | null;
  createdAt: string;
  ackedAt: string | null;
  ackedBy: AlertPerson | null;
  resolvedAt: string | null;
  /** how it was resolved: by itself (the condition cleared) or closed by a person */
  resolution: 'auto' | 'manual' | null;
  /** where it happened; null = a system alert (only a national account may act on it) */
  location: { id: number; name: string; regencyName: string | null } | null;
  reader: { gwId: string; label: string | null } | null;
  cylinder: { id: number; serialNo: string } | null;
  shipment: { id: number; number: string | null } | null;
  /** what the caller may do to it now (the server's answer: permission, scope, state, type) */
  can?: { ack: boolean; close: boolean };
}
export type AlertPage = CursorPage<Alert>;

export interface AlertTimelineEntry {
  at: string;
  kind: 'raised' | 'acked' | 'resolved' | 'reopened';
  by?: AlertPerson | null;
  note?: string | null;
}
/** GET /alerts/:id: the row and its history (oldest first). */
export interface AlertDetail extends Alert { timeline: AlertTimelineEntry[] }

/** GET /alerts/summary: the caller's scope only. The bell counts `needsAction`. */
export interface AlertSummary {
  needsAction: Record<Severity, number>;
  handling: number;
}
export const needsActionTotal = (s: AlertSummary | null | undefined) => (s ? s.needsAction.critical + s.needsAction.warning + s.needsAction.info : 0);

/**
 * The `alert` stream message: one alert opened, taken or resolved, as stored (its title as it was raised). Lists and
 * counts refetch on it; only the critical toast reads it directly.
 */
export interface AlertEvent {
  id: number;
  type: AlertType;
  severity: Severity;
  title: string | null;
  createdAt: string;
  resolvedAt: string | null;
  ackedAt: string | null;
  gwId?: string | null;
  epc?: string | null;
  detail?: Record<string, unknown>;
  /** where it happened (null: a system alert) */
  locationId?: number | null;
}

/** List filters; several values of one filter are sent comma-separated. `from`/`to` are WIB days. */
export interface AlertFilter {
  state?: AlertState | '';
  severity?: string;
  type?: string;
  provinceCode?: string;
  regencyCode?: string;
  locationId?: number;
  gwId?: string;
  /** about this cylinder (the cylinder page's "Lihat semua") */
  cylinderId?: number;
  /** about this Surat Jalan (the note's page's "Lihat semua") */
  shipmentId?: number;
  q?: string;
  from?: string;
  to?: string;
}

export const alertPaths = {
  list: (f: AlertFilter, cursor: Cursor, limit: number) => `/alerts${query({ ...f, ...cursor, limit })}`,
  detail: (id: number) => `/alerts/${id}`,
  summary: '/alerts/summary',
};

/** Take an alert (needs action → handling). Idempotent: a second ack keeps the first person. */
export const ackAlert = (id: number, note?: string) => send<Alert>(`/alerts/${id}/ack`, note ? { note } : {});
/** Close it by hand (types that never clear by themselves; an admin any type); the note is required. */
export const closeAlert = (id: number, note: string) => send<Alert>(`/alerts/${id}/close`, { note });
/** Take several at once (at most 200, the backend's limit; a page holds at most 100); the answer says how many were still waiting. */
export const ackAlerts = (ids: number[]) => send<{ acked: number }>('/alerts/ack', { ids });
